import React, { useEffect, useState } from 'react';
import { Sparkles, Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { validateEmail, parseAuthError, authStorage } from '../services/authService';

interface LoginViewProps {
  onAuthSuccess: (user: User) => void;
  isDark: boolean;
  onShowSignUp?: () => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onAuthSuccess, isDark, onShowSignUp }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = authStorage.getRememberMeEmail();
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) {
        setError(parseAuthError(error) || 'Failed to sign in with Google. Please try again.');
      }
    } catch (err) {
      setError(parseAuthError(err) || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error);
      return false;
    }
    setEmailError(null);

    if (!password) {
      setError('Password is required');
      return false;
    }

    return true;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        setError(parseAuthError(error));
      } else if (data.user) {
        // Save email if remember me is checked
        if (rememberMe) {
          authStorage.setRememberMe(email);
        } else {
          authStorage.clearRememberMe();
        }

        setSuccess('✓ Signed in successfully! Redirecting...');
        setTimeout(() => {
          onAuthSuccess(data.user);
        }, 500);
      }
    } catch (err) {
      setError(parseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setEmailError('Please enter your email to reset password');
      return;
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        setError(parseAuthError(error));
      } else {
        setSuccess('✓ Password reset email sent! Check your inbox.');
      }
    } catch (err) {
      setError(parseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
      isDark
        ? 'bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950'
        : 'bg-gradient-to-br from-zinc-50 via-white to-zinc-50'
    }`}>
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 via-orange-600 to-rose-600 flex items-center justify-center shadow-lg ${
            isDark ? 'shadow-red-500/30' : 'shadow-red-300/40'
          }`}>
            <Sparkles className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent mb-2">
            Welcome Back
          </h2>
          <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
            Sign in to continue your learning journey
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className={`mb-6 flex items-center gap-3 p-4 rounded-lg ${isDark ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-100 border border-green-300'}`}>
            <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
            <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>{success}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={`mb-6 flex items-center gap-3 p-4 rounded-lg ${isDark ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-100 border border-red-300'}`}>
            <AlertCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleEmailLogin} className={`space-y-5 rounded-2xl shadow-xl p-8 mb-6 ${
          isDark 
            ? 'bg-zinc-900/50 border border-zinc-800' 
            : 'bg-white/80 border border-zinc-200'
        }`}>
          {/* Email Input */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              Email Address
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                emailError ? 'text-red-500' : isDark ? 'text-zinc-500' : 'text-zinc-400'
              }`} />
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-colors ${
                  emailError
                    ? 'border-red-500 focus:border-red-600'
                    : isDark
                    ? 'border-zinc-700 bg-zinc-800 focus:border-red-500'
                    : 'border-zinc-300 bg-white focus:border-red-500'
                } focus:outline-none focus:ring-2 focus:ring-red-500/20`}
                required
                disabled={loading}
              />
            </div>
            {emailError && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {emailError}
              </p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Password
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
              >
                Forgot?
              </button>
            </div>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                isDark ? 'text-zinc-500' : 'text-zinc-400'
              }`} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-colors ${
                  isDark
                    ? 'border-zinc-700 bg-zinc-800 focus:border-red-500'
                    : 'border-zinc-300 bg-white focus:border-red-500'
                } focus:outline-none focus:ring-2 focus:ring-red-500/20`}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isDark
                    ? 'text-zinc-400 hover:text-zinc-300'
                    : 'text-zinc-400 hover:text-zinc-600'
                }`}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="remember"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 rounded border-zinc-300 accent-red-600 cursor-pointer"
            />
            <label htmlFor="remember" className={`text-sm cursor-pointer ${
              isDark ? 'text-zinc-400' : 'text-zinc-600'
            }`}>
              Remember this email
            </label>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-red-600 to-rose-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className={`w-full py-3 rounded-lg font-semibold text-lg shadow-md transition-all flex items-center justify-center gap-2 mb-6 ${
            isDark
              ? 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
              : 'bg-white border border-zinc-300 text-zinc-800 hover:bg-zinc-50'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"></path>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"></path>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"></path>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"></path>
          </svg>
          Continue with Google
        </button>

        {/* Sign Up Link */}
        <div className={`text-center text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onShowSignUp}
            disabled={loading}
            className="text-red-600 hover:text-red-700 font-semibold transition-colors"
          >
            Create one
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
